import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  AddButton,
  ThinDropdownWithSearch,
} from "../../../components/common/GeneralComponent";
import { fetchData, submitFormWithErrors } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IP_DISTRICT_MAP, ROLE_TO_IP_NAME } from "../ipDistrictMapping";

const AdministrativeInfo = ({
  grantId,
  grantData,
  onGrantCreated,
  onNext,
  isEditMode,
  showNextOnly,
  onStoreFormData,
  onValidationChange,
  initialData,
  onGrantUpdated,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user: loggedInUser } = useSelector((state) => state.auth);
 
  const userIpName = ROLE_TO_IP_NAME[loggedInUser?.user_role_name] || null;
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [ucs, setUcs] = useState([]);
  const [cycles, setCycles] = useState([]);

const validationSchema = Yup.object({
  cycle_id: Yup.string().required("Cycle is required"),
  district_id: Yup.string().required("District is required"),
  tehsil_id: Yup.string(),
  // uc_id: Yup.string().required("UC is required"),
  // uc_name: Yup.string().optional(),
  village_name: Yup.string(),
  latitude: Yup.number()
    .transform((value, original) => (original === "" ? null : value))
    .nullable()
    .min(-90, "Invalid latitude")
    .max(90, "Invalid latitude"),
  longitude: Yup.number()
    .transform((value, original) => (original === "" ? null : value))
    .nullable()
    .min(-180, "Invalid longitude")
    .max(180, "Invalid longitude"),
});

  const formik = useFormik({
    initialValues: {
      cycle_id: initialData?.cycle_id || grantData?.cycle_id || "",
      district_id: initialData?.district_id || grantData?.district_id || "",
      tehsil_id: initialData?.tehsil_id || grantData?.tehsil_id || "",
      // uc_id: initialData?.uc_id || grantData?.uc_id || "",
      uc_name: initialData?.uc_name || grantData?.uc_name || "",
      village_name: initialData?.village_name || grantData?.village_name || "",
      latitude: initialData?.latitude || grantData?.latitude || "",
      longitude: initialData?.longitude || grantData?.longitude || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      await handleFormSubmit(values);
    },
  });

  const handleFormSubmit = async (values) => {
    if (loading) return;
    
    setLoading(true);
    try {
      if (isEditMode) {
        const response = await fetchData(
          `micro-grants/${grantId}`,
          "PUT",
          values,
          {},
          {},
          navigate,
          "",
          dispatch
        );
        
        if (response) {
          toast.success("Administrative information updated successfully");
          if (onGrantUpdated) {
            onGrantUpdated();
          }
        }
      } else {
        if (onStoreFormData) {
          onStoreFormData(values);
        }
        toast.success("Administrative information saved locally");
        if (onNext) {
          onNext();
        }
      }
    } catch (error) {
      console.error("Error saving administrative info:", error);
      toast.error("Failed to save administrative information");
    } finally {
      setLoading(false);
    }
  };

  const handleNextClick = () => {
    if (loading) return;
    formik.validateForm().then((validationErrors) => {
      if (Object.keys(validationErrors).length > 0) {
        formik.setTouched(
          Object.keys(formik.values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {})
        );
        return;
      }
      proceedWithNavigation();
    });
  };

  const proceedWithNavigation = () => {
    if (onStoreFormData) {
      onStoreFormData(formik.values);
    }
    
    toast.success("Administrative information saved");
    
    if (onNext) {
      onNext();
    }
  };

  useEffect(() => {
    if (onValidationChange) {
      const timeoutId = setTimeout(() => {
        const errors = formik.validateForm();
        if (typeof errors === 'object' && errors.then) {
          errors.then((validationErrors) => {
            onValidationChange(Object.keys(validationErrors).length === 0);
          });
        } else {
          onValidationChange(Object.keys(errors).length === 0);
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [formik.values, onValidationChange]);

  useEffect(() => {
    if (onStoreFormData && formik.values && !isEditMode) {
      const timeoutId = setTimeout(() => {
        onStoreFormData(formik.values);
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [formik.values, onStoreFormData, isEditMode]);

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await fetchData(
          "districts",
          "GET",
          null,
          {},
          { status: 1 },
          navigate,
          "",
          dispatch
        );

        setDistricts(response?.data || []);
      } catch (error) {
        console.error("Error fetching districts:", error);
      }
    };

    fetchDistricts();
  }, []);

  useEffect(() => {
    const fetchCycles = async () => {
      try {
        const response = await fetchData(
          "cycle",
          "GET",
          null,
          {},
          { order: "ASC", sortBy: "id" },
          navigate,
          "",
          dispatch
        );
        setCycles(response?.data || []);
      } catch (error) {
        console.error("Error fetching cycles:", error);
      }
    };

    fetchCycles();
  }, [navigate, dispatch]);

  useEffect(() => {
    if (formik.values.district_id) {
      fetchTehsilsByDistrict(formik.values.district_id);
    } else {
      setTehsils([]);
      setUcs([]);
    }
  }, [formik.values.district_id]);

  useEffect(() => {
    if (formik.values.tehsil_id) {
      fetchUcsByTehsil(formik.values.tehsil_id);
    } else {
      setUcs([]);
    }
  }, [formik.values.tehsil_id]);

  const fetchTehsilsByDistrict = async (districtId) => {
    try {
      const response = await fetchData(
        `tehsils?district_id=${districtId}`,
        "GET",
        null,
        {},
        { status: 1 },
        navigate,
        "",
        dispatch
      );
      setTehsils(response?.data || []);
    } catch (error) {
      console.error("Error fetching tehsils:", error);
      setTehsils([]);
    }
  };

  const fetchUcsByTehsil = async (tehsilId) => {
    try {
      const response = await fetchData(
        `ucs?tehsil_id=${tehsilId}`,
        "GET",
        null,
        {},
        { status: 1 },
        navigate,
        "",
        dispatch
      );
      setUcs(response?.data || []);
    } catch (error) {
      console.error("Error fetching UCs:", error);
      setUcs([]);
    }
  };

  const handleDistrictChange = (value) => {
    formik.setValues((prev) => ({
      ...prev,
      district_id: value,
      tehsil_id: "",
      uc_id: "",
      village_name: "",
    }));
  };

  const allowedDistrictNames = userIpName ? IP_DISTRICT_MAP[userIpName] || [] : null;
  const filteredDistricts = allowedDistrictNames
    ? districts.filter((d) =>
        allowedDistrictNames.includes(d.district_name || d.District_Name)
      )
    : districts;

  useEffect(() => {
    if (!userIpName || districts.length === 0) return;

    const alreadyValid = filteredDistricts.some(
      (d) => String(d.district_id || d.District_Id) === String(formik.values.district_id)
    );
    if (alreadyValid) return;

    if (filteredDistricts.length === 1) {
      handleDistrictChange(
        filteredDistricts[0].district_id || filteredDistricts[0].District_Id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIpName, districts]);

  const handleTehsilChange = (value) => {
    formik.setValues((prev) => ({
      ...prev,
      tehsil_id: value,
      uc_id: "",
      village_name: "",
    }));
  };

  const handleUcChange = (value) => {
    formik.setValues((prev) => ({
      ...prev,
      uc_id: value,
      village_name: "",
    }));
  };

  const handleVillageNameChange = (e) => {
    formik.setFieldValue("village_name", e.target.value);
  };

  useEffect(() => {
    if (grantData && grantData.district_id) {
      fetchTehsilsByDistrict(grantData.district_id);
    }
  }, [grantData?.district_id]);

  useEffect(() => {
    if (grantData && grantData.tehsil_id) {
      fetchUcsByTehsil(grantData.tehsil_id);
    }
  }, [grantData?.tehsil_id]);

  return (
    <form onSubmit={submitFormWithErrors(formik)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <ThinDropdownWithSearch
            label="Cycle"
            options={cycles.map((cycle) => ({
              value: cycle.id || cycle.cycle_id,
              label: cycle.cycle_name || cycle.cycleName || `Cycle ${cycle.id || cycle.cycle_id}`,
            }))}
            value={formik.values.cycle_id}
            onChange={(value) => formik.setFieldValue("cycle_id", value)}
            error={formik.touched.cycle_id && formik.errors.cycle_id}
          />
          {formik.touched.cycle_id && formik.errors.cycle_id && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.cycle_id}
            </div>
          )}
        </div>

        <div>
          <ThinDropdownWithSearch
            label="District"
            options={filteredDistricts.map((d) => ({
              value: d.district_id || d.District_Id,
              label: d.district_name || d.District_Name,
            }))}
            value={formik.values.district_id}
            onChange={handleDistrictChange}
            disabled={!!userIpName}
            error={formik.touched.district_id && formik.errors.district_id}
          />
          {formik.touched.district_id && formik.errors.district_id && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.district_id}
            </div>
          )}
        </div>

        <div>
          <ThinDropdownWithSearch
            label="Tehsil"
            options={tehsils.map((t) => ({
              value: t.tehsil_id || t.Tehsil_Id,
              label: t.tehsil_name || t.Tehsil_Name,
            }))}
            value={formik.values.tehsil_id}
            onChange={handleTehsilChange}
            disabled={!formik.values.district_id}
            error={formik.touched.tehsil_id && formik.errors.tehsil_id}
            required={false}
          />
          {formik.touched.tehsil_id && formik.errors.tehsil_id && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.tehsil_id}
            </div>
          )}
        </div>

        {/* <div>
          <ThinDropdownWithSearch
            label="UC"
            options={ucs.map((u) => ({
              value: u.uc_id,
              label: u.uc_name,
            }))}
            value={formik.values.uc_id}
            onChange={handleUcChange}
            disabled={!formik.values.tehsil_id}
            error={formik.touched.uc_id && formik.errors.uc_id}
          />
          {formik.touched.uc_id && formik.errors.uc_id && (
            <div className="text-red-500 text-sm mt-1">{formik.errors.uc_id}</div>
          )}
        </div> */}

        <div>
          <ThinTextField
            label="UC"
            name="uc_name"
            value={formik.values.uc_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isRequired = {false}
            placeholder="Enter UC (Optional)"
            error={formik.touched.uc_name && formik.errors.uc_name}
          />
        </div>

        <div>
          <ThinTextField
            label="Village Name"
            name="village_name"
            value={formik.values.village_name}
            onChange={handleVillageNameChange}
            onBlur={formik.handleBlur}
            error={formik.touched.village_name && formik.errors.village_name}
            placeholder="Enter village name"
            isRequired={false}
          />
          {formik.touched.village_name && formik.errors.village_name && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.village_name}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Latitude"
            isRequired={false}
            name="latitude"
            type="number"
            step="0.000001"
            value={formik.values.latitude}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.latitude && formik.errors.latitude}
            placeholder="Enter latitude (e.g., 24.123456)"
          />
          {formik.touched.latitude && formik.errors.latitude && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.latitude}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Longitude"
            isRequired={false}
            name="longitude"
            type="number"
            step="0.000001"
            value={formik.values.longitude}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.longitude && formik.errors.longitude}
            placeholder="Enter longitude (e.g., 67.123456)"
          />
          {formik.touched.longitude && formik.errors.longitude && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.longitude}
            </div>
          )}
        </div>
      </div>

      <div className="pt-4 flex justify-end gap-3">
        {showNextOnly && !isEditMode && (
          <button
            type="button"
            onClick={handleNextClick}
            className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Processing..." : "Next"}
          </button>
        )}
        {isEditMode && (
          <AddButton
            text="Update"
            type="submit"
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </form>
  );
};

export default AdministrativeInfo;